import { of, timer } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';
import { Component, DestroyRef, Inject, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { EntityConfig } from '../../core/config/entity-config';
import { resolveDefaultRateForCategoryName } from '../../core/config/instructor-rates.config';
import { formatInputDate } from '../../core/utils/date-format.util';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface CreateResourceDialogData {
  entity: EntityConfig;
  selectOptions?: Record<string, Array<{ value: string | number; label: string }>>;
  mode?: 'create' | 'edit';
  initialValues?: Record<string, unknown>;
  statusOptions?: string[];
  courseTypes?: Record<string, 'STANDARD' | 'AD_HOC'>;
}

@Component({
  selector: 'app-create-resource-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    TranslatePipe
    ,EnterSubmitDirective
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  template: `
    <h2 mat-dialog-title>{{ dialogTitlePrefix() | t }} {{ entityAddLabel() }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="submit()">
    <mat-dialog-content>
      @if (isInstructorEntity()) {
        <div class="create-form create-form-instructor">
          <section class="form-section">
            <h3>{{ getSectionLabel('basic') }}</h3>
            <div class="section-grid">
              @for (field of getInstructorFields('basic'); track field.key) {
                <mat-form-field>
                  <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                  @if (isDateField(field.type)) {
                    <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                  } @else {
                    <input matInput [type]="field.type" [formControlName]="field.key" />
                  }
                  @if (isDateField(field.type)) {
                    <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                  }
                  <mat-datepicker #picker></mat-datepicker>
                  @if (hasControlError(field.key)) {
                    <mat-error>{{ getControlError(field.key) }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </section>

          <section class="form-section">
            <h3>{{ getSectionLabel('contact') }}</h3>
            <div class="section-grid">
              @for (field of getInstructorFields('contact'); track field.key) {
                <mat-form-field>
                  <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                  @if (isDateField(field.type)) {
                    <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                  } @else {
                    <input matInput [type]="field.type" [formControlName]="field.key" />
                  }
                  @if (isDateField(field.type)) {
                    <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                  }
                  <mat-datepicker #picker></mat-datepicker>
                  @if (hasControlError(field.key)) {
                    <mat-error>{{ getControlError(field.key) }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </section>

          <section class="form-section form-section-full">
            <h3>{{ getSectionLabel('address') }}</h3>
            <div class="section-grid">
              @for (field of getInstructorFields('address'); track field.key) {
                <mat-form-field>
                  <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                  @if (isSelectField(field.key, field.type)) {
                    <mat-select [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                      @for (option of getSelectOptions(field.key); track option.value) {
                        <mat-option [value]="option.value">{{ option.label }}</mat-option>
                      }
                    </mat-select>
                  } @else if (isDateField(field.type)) {
                    <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                  } @else {
                    <input matInput [type]="field.type" [formControlName]="field.key" />
                  }
                  @if (isDateField(field.type)) {
                    <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                  }
                  <mat-datepicker #picker></mat-datepicker>
                  @if (hasControlError(field.key)) {
                    <mat-error>{{ getControlError(field.key) }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </section>

          <section class="form-section form-section-full">
            <h3>{{ getSectionLabel('licence') }}</h3>
            <div class="section-grid">
              @for (field of getInstructorFields('licence'); track field.key) {
                <mat-form-field>
                  <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                  <mat-select [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                    @for (option of getSelectOptions(field.key); track option.value) {
                      <mat-option [value]="option.value">{{ option.label }}</mat-option>
                    }
                  </mat-select>
                  @if (hasControlError(field.key)) {
                    <mat-error>{{ getControlError(field.key) }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </section>

          <section class="form-section form-section-full">
            <h3>{{ getSectionLabel('rates') }}</h3>
            <div class="section-grid">
              @for (item of selectedInstructorCategories(); track item.value) {
                <mat-form-field>
                  <mat-label>{{ item.label }} (PLN/h)</mat-label>
                  <input matInput type="number" min="0" step="1" [formControlName]="getRateControlKey(item.value)" />
                  @if (hasControlError(getRateControlKey(item.value))) {
                    <mat-error>{{ getControlError(getRateControlKey(item.value)) }}</mat-error>
                  }
                </mat-form-field>
              } @empty {
                <p class="rates-empty">{{ localization.translate('createDialog.rates.selectCategoryFirst') }}</p>
              }
            </div>
          </section>
        </div>
      } @else {
        @if (shouldUseGroupedSections()) {
          <div class="create-form create-form-sectioned">
            <section class="form-section form-section-full">
              <h3>{{ getSectionLabel('basic') }}</h3>
              @if (isStudentEntity()) {
                <p class="identity-help">{{ 'student.identity.help' | t }}</p>
              }
              <div class="section-grid">
                @for (field of getNonInstructorFields('basic'); track field.key) {
                  <mat-form-field [class.pesel-edit-field]="field.key === 'personalIdentificationNumber' && isEditMode() && hasStoredPesel() && !peselEditEnabled()">
                    <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                    @if (isSelectField(field.key, field.type)) {
                      <mat-select [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                        @for (option of getSelectOptions(field.key); track option.value) {
                          <mat-option [value]="option.value">{{ option.label }}</mat-option>
                        }
                      </mat-select>
                    } @else if (isDateField(field.type)) {
                      <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                    } @else if (field.key === 'personalIdentificationNumber') {
                      <input matInput type="text" inputmode="numeric" maxlength="11" autocomplete="off" data-private="true" [readonly]="isEditMode() && hasStoredPesel() && !peselEditEnabled()" [formControlName]="field.key" />
                      @if (isEditMode() && hasStoredPesel() && !peselEditEnabled()) {
                        <button mat-icon-button type="button" class="pesel-edit-button" (click)="activatePeselEdit()" [attr.aria-label]="'student.identity.editPesel' | t">
                          <mat-icon>edit</mat-icon>
                        </button>
                      }
                    } @else {
                      <input matInput [type]="field.type" [formControlName]="field.key" />
                    }
                    @if (isDateField(field.type)) {
                      <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                    }
                    <mat-datepicker #picker></mat-datepicker>
                    @if (hasControlError(field.key)) {
                      <mat-error>{{ getControlError(field.key) }}</mat-error>
                    }
                  </mat-form-field>
                }
              </div>
              @if (hasIdentityError()) { <p class="form-error">{{ 'validation.studentIdentity' | t }}</p> }
            </section>

            @if (getNonInstructorFields('contact').length > 0) {
              <section class="form-section form-section-full">
                <h3>{{ getSectionLabel('contact') }}</h3>
                <div class="section-grid">
                  @for (field of getNonInstructorFields('contact'); track field.key) {
                    <mat-form-field>
                      <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                      @if (isSelectField(field.key, field.type)) {
                        <mat-select [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                          @for (option of getSelectOptions(field.key); track option.value) {
                            <mat-option [value]="option.value">{{ option.label }}</mat-option>
                          }
                        </mat-select>
                      } @else if (isDateField(field.type)) {
                        <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                      } @else {
                        <input matInput [type]="field.type" [formControlName]="field.key" />
                      }
                      @if (isDateField(field.type)) {
                        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                      }
                      <mat-datepicker #picker></mat-datepicker>
                      @if (hasControlError(field.key)) {
                        <mat-error>{{ getControlError(field.key) }}</mat-error>
                      }
                    </mat-form-field>
                  }
                </div>
              </section>
            }

            @if (getNonInstructorFields('address').length > 0) {
              <section class="form-section form-section-full">
                <h3>{{ getSectionLabel('address') }}</h3>
                <div class="section-grid">
                  @for (field of getNonInstructorFields('address'); track field.key) {
                    <mat-form-field>
                      <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                      @if (isSelectField(field.key, field.type)) {
                        <mat-select [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                          @for (option of getSelectOptions(field.key); track option.value) {
                            <mat-option [value]="option.value">{{ option.label }}</mat-option>
                          }
                        </mat-select>
                      } @else if (isDateField(field.type)) {
                        <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                      } @else {
                        <input matInput [type]="field.type" [formControlName]="field.key" />
                      }
                      @if (isDateField(field.type)) {
                        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                      }
                      <mat-datepicker #picker></mat-datepicker>
                      @if (hasControlError(field.key)) {
                        <mat-error>{{ getControlError(field.key) }}</mat-error>
                      }
                    </mat-form-field>
                  }
                </div>
              </section>
            }
          </div>
        } @else {
          <div class="create-form">
            @for (field of data.entity.createFields; track field.key) {
              <mat-form-field>
                <mat-label>{{ getFieldLabel(field.key, field.label) }}</mat-label>
                @if (usesStudentSearch() && field.key === 'studentId') {
                  <input matInput data-testid="lesson-student-search" [formControl]="studentSearch" [matAutocomplete]="studentAutocomplete" />
                  <mat-autocomplete #studentAutocomplete="matAutocomplete" [displayWith]="displayStudent" (optionSelected)="selectStudent($event.option.value)">
                    @for (student of studentResults(); track student.value) {
                      <mat-option [value]="student">{{ student.label }}</mat-option>
                    }
                  </mat-autocomplete>
                } @else if (isSelectField(field.key, field.type)) {
                  <mat-select [disabled]="usesStudentSearch() && field.key === 'courseInstanceId' && (coursesLoading() || !form.controls['studentId'].value)" [formControlName]="field.key" [multiple]="isMultipleField(field.key)">
                    @for (option of getSelectOptions(field.key); track option.value) {
                      <mat-option [value]="option.value">{{ option.label }}</mat-option>
                    }
                  </mat-select>
                } @else if (isDateField(field.type)) {
                  <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
                } @else {
                  <input matInput [type]="field.type" [formControlName]="field.key" />
                }
                @if (usesStudentSearch() && field.key === 'studentId') {
                  <mat-hint>{{ (studentSearchFailed() ? 'api.mutationError' : 'lessonDialog.studentSearchHint') | t }}</mat-hint>
                }
                @if (usesStudentSearch() && field.key === 'courseInstanceId' && coursesFailed()) {
                  <mat-hint>{{ 'api.mutationError' | t }}</mat-hint>
                }
                @if (isDateField(field.type)) {
                  <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                }
                <mat-datepicker #picker></mat-datepicker>
                @if (hasControlError(field.key)) {
                  <mat-error>{{ getControlError(field.key) }}</mat-error>
                }
              </mat-form-field>
            }
          </div>
        }
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary" [disabled]="form.invalid || coursesLoading() || coursesFailed()">{{ dialogTitlePrefix() | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    :host {
      display: flex;
      flex-direction: column;
      max-height: 100%;
      min-height: 0;
    }
    mat-dialog-content {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
      overflow: auto;
    }
    mat-dialog-actions {
      margin-top: auto;
      border-top: 1px solid #d7e1f5;
      background: #fff;
      padding-top: 12px;
    }
    .create-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      min-width: 980px;
    }
    .create-form mat-form-field {
      width: 100%;
    }
    .create-form-instructor {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      min-width: 920px;
    }
    .form-section {
      border: 1px solid #d7e1f5;
      border-radius: 12px;
      padding: 14px;
      background: #f9fbff;
    }
    .form-section h3 {
      margin: 0 0 10px;
      font-size: 1rem;
      font-weight: 700;
      color: #2b3f5d;
    }
    .form-section-full {
      grid-column: 1 / -1;
    }
    .section-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
    .rates-empty {
      margin: 0;
      color: #5a6f8d;
    }
    .identity-help{margin:0 0 12px;color:#4d6280}.form-error{margin:0;color:#ba1a1a;font-size:.82rem}
    .pesel-edit-field{position:relative}.pesel-edit-field .pesel-edit-button{position:absolute;right:4px;top:4px;z-index:2;color:var(--mat-sys-primary,#007c5d)}
    @media (max-width: 1024px) and (orientation: portrait), (max-width: 767px) {
      .create-form {
        grid-template-columns: 1fr;
        min-width: 320px;
      }
      .create-form-instructor {
        grid-template-columns: 1fr;
        min-width: 320px;
      }
      .section-grid {
        grid-template-columns: 1fr;
      }
    }
  `
})
export class CreateResourceDialogComponent {
  readonly form;
  private readonly dataService = inject(InMemoryDataService);
  readonly studentSearch = new FormControl<string | { value: string | number; label: string }>('', { nonNullable: true });
  readonly studentSearchFailed = signal(false);
  readonly assignedCourses = signal<Array<{ value: string | number; label: string }>>([]);
  readonly coursesLoading = signal(false);
  readonly coursesFailed = signal(false);
  readonly studentResults = signal<Array<{ value: string | number; label: string }>>([]);
  readonly displayStudent = (value: string | { label: string }): string => typeof value === 'string' ? value : value?.label ?? '';

  usesStudentSearch(): boolean {
    return this.data.entity.key === 'lessons' && this.isEditMode();
  }

  selectStudent(student: { value: string | number; label: string }): void {
    const control = this.form.controls['studentId'];
    control.setValue(student.value);
    control.markAsDirty();
    control.markAsTouched();
  }

  readonly peselEditEnabled = signal(true);
  readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contactFieldKeys = new Set(['email', 'mobile']);
  private readonly addressFieldKeys = new Set(['streetName', 'buildingNumber', 'postalCode', 'city', 'country']);

  constructor(
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<CreateResourceDialogComponent, Record<string, unknown> | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: CreateResourceDialogData
  ) {
    this.form = this.buildForm();
    if (this.usesStudentSearch()) {
      let initialLoad = true;
      this.form.controls['studentId'].valueChanges.pipe(
        startWith(this.form.controls['studentId'].value),
        switchMap(studentId => {
          const previousCourse = initialLoad ? this.form.controls['courseInstanceId'].value : '';
          initialLoad = false;
          this.form.controls['courseInstanceId'].setValue('');
          this.assignedCourses.set([]);
          this.coursesFailed.set(false);
          this.coursesLoading.set(false);
          if (typeof studentId !== 'string' && typeof studentId !== 'number' || studentId === '') return of([]);
          this.coursesLoading.set(true);
          return this.dataService.fetchAssignedStudentCourses$(studentId).pipe(
            map(courses => {
              const options = courses.map(course => ({ value: course.id, label: course.name }));
              const current = options.find(course => String(course.value) === String(previousCourse));
              if (current) this.form.controls['courseInstanceId'].setValue(current.value);
              return options;
            }),
            catchError(() => { this.coursesFailed.set(true); return of([]); })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(courses => { this.assignedCourses.set(courses); this.coursesLoading.set(false); });
      const id = this.data.initialValues?.['studentId'];
      const current = this.getSelectOptions('studentId').find(option => String(option.value) === String(id));
      if (current) this.studentSearch.setValue(current, { emitEvent: false });
      this.studentSearch.valueChanges.pipe(
        switchMap(value => {
          this.studentResults.set([]);
          this.studentSearchFailed.set(false);
          if (typeof value !== 'string') return of([]);
          this.form.controls['studentId'].setValue('');
          const query = value.trim();
          if (query.length < 3) return of([]);
          return timer(300).pipe(
            switchMap(() => this.dataService.fetchCollectionPage$('students', { page: 1, pageSize: 20, filters: { query } })),
            map(page => page.items.map(student => ({ value: student.id, label: `${student.name} ${student.surname}` }))),
            catchError(() => { this.studentSearchFailed.set(true); return of([]); })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(results => this.studentResults.set(results));
    }
    if (this.isStudentEntity() && this.isEditMode() && this.hasStoredPesel()) {
      const peselControl = this.form.controls['personalIdentificationNumber'];
      peselControl?.setValue(`${this.data.initialValues?.['personalIdentificationNumberMasked'] ?? ''}`);
      peselControl?.clearValidators();
      peselControl?.updateValueAndValidity({ emitEvent: false });
      this.peselEditEnabled.set(false);
    }
    if (this.isInstructorEntity()) {
      this.syncInstructorRateControls();
      this.form.controls['licenceCategories']?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.syncInstructorRateControls());
    }
    if (this.isCourseInstanceEntity()) {
      this.form.controls['courseId']?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.syncCourseInstancePricing());
      this.syncCourseInstancePricing();
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const output: Record<string, unknown> = {};
    Object.entries(this.form.getRawValue()).forEach(([key, value]) => {
      if (key.startsWith('hourlyRateCategory_')) {
        return;
      }
      const field = this.findField(key);
      if (this.isStudentEntity() && this.isEditMode() && key === 'personalIdentificationNumber') {
        if (!this.peselEditEnabled()) return;
        const replacementPesel = `${value ?? ''}`.trim();
        output[key] = replacementPesel || null;
        return;
      }
      output[key] = field?.type === 'date' ? this.toDateOutputValue(value) : value;
    });

    if (this.isInstructorEntity()) {
      output['hourlyRateByCategory'] = this.collectInstructorRates();
    }
    if (this.isCourseInstanceEntity()) {
      const adHoc = this.selectedCourseType() === 'AD_HOC';
      output['billingModel'] = adHoc ? 'HOURLY' : 'FIXED_PRICE';
      output['price'] = adHoc ? 0 : Number(output['price']);
      output['studentHourlyPrice'] = adHoc ? Number(output['studentHourlyPrice']) : null;
    }

    this.form.controls['personalIdentificationNumber']?.setValue('');

    this.dialogRef.close(output);
  }

  isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  isStudentEntity(): boolean { return this.data.entity.key === 'students'; }
  hasStoredPesel(): boolean { return Boolean(this.data.initialValues?.['personalIdentificationNumberMasked']); }
  activatePeselEdit(): void {
    const control = this.form.controls['personalIdentificationNumber'];
    control?.setValue('');
    control?.setValidators(peselValidator());
    control?.updateValueAndValidity();
    control?.markAsTouched();
    this.peselEditEnabled.set(true);
    this.form.setValidators(studentIdentityValidator(false));
    this.form.updateValueAndValidity();
  }
  hasIdentityError(): boolean { return this.isStudentEntity() && this.form.hasError('studentIdentity') && (this.form.touched || this.form.dirty); }

  dialogTitlePrefix(): string {
    return this.isEditMode() ? 'common.edit' : 'common.add';
  }

  statusOptions(): string[] {
    return this.data.statusOptions ?? [];
  }

  getSelectOptions(fieldKey: string): Array<{ value: string | number; label: string }> {
    if (this.usesStudentSearch() && fieldKey === 'courseInstanceId') return this.assignedCourses();
    if (fieldKey === 'courseType') {
      return [
        { value: 'STANDARD', label: this.localization.translate('courseType.standard') },
        { value: 'AD_HOC', label: this.localization.translate('courseType.adHoc') }
      ];
    }
    if (fieldKey === 'billingModel') {
      return [
        { value: 'FIXED_PRICE', label: this.localization.translate('billingModel.fixedPrice') },
        { value: 'HOURLY', label: this.localization.translate('billingModel.hourly') }
      ];
    }
    if (fieldKey === 'durationHours') {
      return [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((value) => ({ value, label: `${value}` }));
    }
    if (fieldKey === 'country') {
      return [{ value: 'PL', label: this.localization.translate('country.PL') }];
    }
    if (fieldKey === 'status') {
      return this.statusOptions().map((status) => ({ value: status, label: this.translateStatus(status) }));
    }
    return this.data.selectOptions?.[fieldKey] ?? [];
  }

  entityTitle(): string {
    const translated = this.localization.translate(`entity.${this.data.entity.key}`);
    return translated === `entity.${this.data.entity.key}` ? this.translateFallbackLabel(this.data.entity.title) : translated;
  }

  entityAddLabel(): string {
    const translated = this.localization.translate(this.data.entity.addLabelKey);
    return translated === this.data.entity.addLabelKey ? this.entityTitle() : translated;
  }

  getFieldLabel(fieldKey: string, fallback: string): string {
    const keyMap: Record<string, string> = {
      name: this.getNameFieldTranslationKey(),
      categoryId: 'common.category',
      categoryIds: 'common.category',
      category: 'common.category',
      categories: 'common.category',
      courseId: this.isCourseInstanceEntity() ? 'common.product' : 'common.course',
      courseInstanceId: 'common.course',
      studentId: 'common.student',
      instructorId: 'common.instructor',
      resourceId: 'common.resource',
      lessonTime: 'field.lessonTime',
      durationHours: 'lessonDialog.durationHours',
      price: 'field.price',
      dateStarted: 'field.dateStarted',
      lessonsNumber: 'field.lessonsNumber',
      theoreticalLessonsNumber: 'field.theoreticalLessonsNumber',
      practicalLessonsNumber: 'field.practicalLessonsNumber',
      theoryHours: 'field.theoreticalLessonsNumber',
      practiceHours: 'field.practicalLessonsNumber',
      licenceCategoryIds: 'field.licenceCategoryIds',
      licenceCategories: 'field.licenceCategoryIds'
    };
    const translationKey = keyMap[fieldKey] ?? `field.${fieldKey}`;
    const translated = this.localization.translate(translationKey);
    return translated === translationKey ? this.translateFallbackLabel(fallback) : translated;
  }

  private translateFallbackLabel(fallback: string): string {
    const translated = this.localization.translate(fallback);
    return translated === fallback ? fallback : translated;
  }

  private getNameFieldTranslationKey(): string {
    switch (this.data.entity.key) {
      case 'students':
      case 'instructors':
        return 'field.firstName';
      case 'courseInstances':
      case 'courses':
        return 'field.courseName';
      case 'resources':
        return 'field.resourceName';
      case 'categories':
        return 'field.categoryName';
      case 'organisations':
        return 'field.organisationName';
      default:
        return 'field.name';
    }
  }

  isCourseInstanceEntity(): boolean {
    return this.data.entity.key === 'courseInstances';
  }

  isInstructorEntity(): boolean {
    return this.data.entity.key === 'instructors';
  }

  isMultipleField(fieldKey: string): boolean {
    return fieldKey === 'licenceCategoryIds' || fieldKey === 'licenceCategories' || fieldKey === 'categoryIds' || fieldKey === 'categories';
  }

  isSelectField(fieldKey: string, fieldType: string): boolean {
    return fieldType === 'select' || fieldKey === 'country' || fieldKey === 'durationHours';
  }

  isDateField(fieldType: string): boolean {
    return fieldType === 'date';
  }

  getInstructorFields(section: 'basic' | 'contact' | 'address' | 'licence') {
    const groups: Record<'basic' | 'contact' | 'address' | 'licence', string[]> = {
      basic: ['name', 'surname', 'licenceNumber'],
      contact: ['email', 'mobile'],
      address: ['streetName', 'buildingNumber', 'postalCode', 'city', 'country'],
      licence: ['licenceCategories']
    };
    const keys = new Set(groups[section]);
    return this.data.entity.createFields.filter((field) => keys.has(field.key));
  }

  shouldUseGroupedSections(): boolean {
    if (this.isInstructorEntity()) {
      return false;
    }
    return this.data.entity.createFields.some(
      (field) => this.contactFieldKeys.has(field.key) || this.addressFieldKeys.has(field.key)
    );
  }

  getNonInstructorFields(section: 'basic' | 'contact' | 'address') {
    return this.data.entity.createFields.filter((field) => {
      if (section === 'contact') {
        return this.contactFieldKeys.has(field.key);
      }
      if (section === 'address') {
        return this.addressFieldKeys.has(field.key);
      }
      return !this.contactFieldKeys.has(field.key) && !this.addressFieldKeys.has(field.key);
    });
  }

  getSectionLabel(section: 'basic' | 'contact' | 'address' | 'licence' | 'rates'): string {
    const labels: Record<'basic' | 'contact' | 'address' | 'licence' | 'rates', string> = {
      basic: 'createDialog.section.basic',
      contact: 'createDialog.section.contact',
      address: 'createDialog.section.address',
      licence: 'createDialog.section.licence',
      rates: 'createDialog.section.rates'
    };
    const key = labels[section];
    const translated = this.localization.translate(key);
    return translated === key ? section : translated;
  }

  hasControlError(controlKey: string): boolean {
    const control = this.form.controls[controlKey];
    return Boolean(control?.invalid && (control.dirty || control.touched));
  }

  getControlError(controlKey: string): string {
    const control = this.form.controls[controlKey];
    if (!control) {
      return '';
    }
    if (control.hasError('required')) {
      return this.localization.translate('validation.required');
    }
    if (control.hasError('min')) {
      const error = control.getError('min') as { min?: number } | null;
      return this.localization.translate('validation.min', { min: error?.min ?? 0 });
    }
    if (control.hasError('email')) {
      return this.localization.translate('validation.email');
    }
    if (control.hasError('phone')) {
      return this.localization.translate('validation.phone');
    }
    if (control.hasError('pesel')) {
      return this.localization.translate('validation.pesel');
    }
    if (control.hasError('futureBirthDate')) {
      return this.localization.translate('validation.futureBirthDate');
    }
    if (control.hasError('minimumStudentAge')) {
      return this.localization.translate('validation.minimumStudentAge');
    }
    if (control.hasError('pattern')) {
      return this.localization.translate('validation.invalidFormat');
    }
    return this.localization.translate('validation.invalid');
  }

  translateStatus(status: string): string {
    const translated = this.localization.translate(`status.${status}`);
    if (translated !== `status.${status}`) {
      return translated;
    }
    const fallback = this.localization.translate(`common.${status}`);
    return fallback === `common.${status}` ? status : fallback;
  }

  selectedInstructorCategories(): Array<{ value: string; label: string }> {
    const selectedIds = this.getSelectedInstructorCategoryIds();
    const options = this.getSelectOptions('licenceCategories');
    return options
      .filter((option) => selectedIds.includes(`${option.value}`))
      .map((option) => ({ value: `${option.value}`, label: option.label }));
  }

  getRateControlKey(category: string): string {
    return `hourlyRateCategory_${category.replace(/[^a-zA-Z0-9_]/g, '_')}`;
  }

  private buildForm() {
    const controls: Record<string, FormControl<string | number | Date | Array<string | number>>> = {};
    const initialValues = this.data.initialValues ?? {};
    for (const field of this.data.entity.createFields) {
      const validators = this.getValidators(field.key, field.required, field.type);
      const explicit = initialValues[field.key];
      const initialValue = this.isMultipleField(field.key)
        ? (Array.isArray(explicit) ? explicit : [])
        : field.type === 'date'
          ? formatInputDate(explicit)
          : field.key === 'country'
            ? this.normalizeCountryCode(explicit)
            : ((explicit as string | number | undefined) ?? '');
      controls[field.key] = this.fb.control(initialValue, { nonNullable: true, validators });
    }

    if (this.data.entity.key === 'courseInstances' && !this.isEditMode()) {
      const firstCourse = this.getSelectOptions('courseId')[0];
      if (firstCourse) {
        controls['courseId'].setValue(firstCourse.value);
      }
      controls['billingModel']?.setValue('FIXED_PRICE');
    }

    return this.fb.group(controls, {
      validators: this.data.entity.key === 'students'
        ? [studentIdentityValidator(this.isEditMode() && this.hasStoredPesel())]
        : []
    });
  }

  private findField(fieldKey: string) {
    return this.data.entity.createFields.find((field) => field.key === fieldKey);
  }

  private selectedCourseType(): 'STANDARD' | 'AD_HOC' {
    const courseId = this.form.controls['courseId']?.value;
    return this.data.courseTypes?.[String(courseId)] === 'AD_HOC' ? 'AD_HOC' : 'STANDARD';
  }

  private syncCourseInstancePricing(): void {
    const adHoc = this.selectedCourseType() === 'AD_HOC';
    const billingModel = this.form.controls['billingModel'];
    const price = this.form.controls['price'];
    const hourlyPrice = this.form.controls['studentHourlyPrice'];
    billingModel?.setValue(adHoc ? 'HOURLY' : 'FIXED_PRICE', { emitEvent: false });
    billingModel?.disable({ emitEvent: false });
    if (adHoc) {
      price?.setValue(0, { emitEvent: false });
      price?.clearValidators();
      price?.disable({ emitEvent: false });
      hourlyPrice?.enable({ emitEvent: false });
      hourlyPrice?.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      price?.enable({ emitEvent: false });
      price?.setValidators([Validators.required, Validators.min(0)]);
      hourlyPrice?.setValue('', { emitEvent: false });
      hourlyPrice?.clearValidators();
      hourlyPrice?.disable({ emitEvent: false });
    }
    price?.updateValueAndValidity({ emitEvent: false });
    hourlyPrice?.updateValueAndValidity({ emitEvent: false });
  }

  private toDateOutputValue(value: unknown): string {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, '0');
      const day = `${value.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return formatInputDate(value);
  }

  private getValidators(fieldKey: string, required?: boolean, fieldType?: string): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (required || this.isRequiredForCurrentEntity(fieldKey)) {
      validators.push(Validators.required);
    }
    if (fieldKey === 'email') {
      validators.push(Validators.email);
    }
    if (fieldKey === 'mobile') {
      validators.push(phoneValidator());
    }
    if (fieldKey === 'personalIdentificationNumber') {
      validators.push(peselValidator());
    }
    if (fieldKey === 'birthDate') {
      validators.push(studentBirthDateValidator());
    }
    if (fieldType === 'number') {
      validators.push(Validators.min(0));
    }
    return validators;
  }

  private isRequiredForCurrentEntity(fieldKey: string): boolean {
    return (this.data.entity.key === 'students' || this.data.entity.key === 'instructors') && (fieldKey === 'city' || fieldKey === 'country');
  }

  private normalizeCountryCode(value: unknown): string {
    const raw = `${value ?? ''}`.trim();
    if (!raw || raw.toLowerCase() === 'polska' || raw.toLowerCase() === 'poland') {
      return 'PL';
    }
    return raw.toUpperCase();
  }

  private getSelectedInstructorCategoryIds(): string[] {
    if (!this.isInstructorEntity()) {
      return [];
    }
    const raw = this.form.controls['licenceCategories']?.value ?? this.form.controls['licenceCategoryIds']?.value;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => `${item ?? ''}`).filter((item) => item.length > 0);
  }

  private syncInstructorRateControls(): void {
    const selected = this.selectedInstructorCategories();
    const initialRates =
      this.data.initialValues?.['hourlyRateByCategory'] &&
      typeof this.data.initialValues['hourlyRateByCategory'] === 'object'
        ? (this.data.initialValues['hourlyRateByCategory'] as Record<string, unknown>)
        : undefined;
    const selectedControlKeys = new Set(selected.map((item) => this.getRateControlKey(item.value)));
    Object.keys(this.form.controls)
      .filter((key) => key.startsWith('hourlyRateCategory_') && !selectedControlKeys.has(key))
      .forEach((key) => (this.form as any).removeControl(key));

    selected.forEach((item) => {
      const key = this.getRateControlKey(item.value);
      if (!this.form.controls[key]) {
        const explicit = Number(initialRates?.[item.label]);
        const defaultRate = Number.isFinite(explicit) ? explicit : resolveDefaultRateForCategoryName(item.label);
        (this.form as any).addControl(
          key,
          this.fb.control(defaultRate, { nonNullable: true, validators: [Validators.required, Validators.min(0)] })
        );
      }
    });
  }

  private collectInstructorRates(): Record<string, number> {
    const rates: Record<string, number> = {};
    this.selectedInstructorCategories().forEach((item) => {
      const key = this.getRateControlKey(item.value);
      const value = Number(this.form.controls[key]?.value ?? 0);
      if (Number.isFinite(value) && value >= 0) {
        rates[item.label] = value;
      }
    });
    return rates;
  }
}

function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = `${control.value ?? ''}`.trim();
    if (!value) {
      return null;
    }
    return /^\+?\d{7,15}$/.test(value) ? null : { phone: true };
  };
}

function peselValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = `${control.value ?? ''}`.trim();
    if (!value) {
      return null;
    }
    if (!/^\d{11}$/.test(value)) {
      return { pesel: true };
    }
    const yearPart = Number(value.slice(0, 2));
    const encodedMonth = Number(value.slice(2, 4));
    const day = Number(value.slice(4, 6));
    let century = 1900;
    let month = encodedMonth;
    if (encodedMonth >= 81 && encodedMonth <= 92) { century = 1800; month -= 80; }
    else if (encodedMonth >= 21 && encodedMonth <= 32) { century = 2000; month -= 20; }
    else if (encodedMonth >= 41 && encodedMonth <= 52) { century = 2100; month -= 40; }
    else if (encodedMonth >= 61 && encodedMonth <= 72) { century = 2200; month -= 60; }
    const birthDate = new Date(century + yearPart, month - 1, day);
    if (birthDate.getFullYear() !== century + yearPart || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
      return { pesel: true };
    }
    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    const sum = weights.reduce((total, weight, index) => total + weight * Number(value[index]), 0);
    const checksum = (10 - (sum % 10)) % 10;
    return checksum === Number(value[10]) ? null : { pesel: true };
  };
}

function studentIdentityValidator(hasStoredPesel = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const pesel = `${control.get('personalIdentificationNumber')?.value ?? ''}`.trim();
    const birthDate = control.get('birthDate')?.value;
    return pesel || birthDate || hasStoredPesel ? null : { studentIdentity: true };
  };
}

function studentBirthDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const date = control.value instanceof Date ? control.value : new Date(`${control.value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return { futureBirthDate: true };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date > today) return { futureBirthDate: true };
    const minimum = new Date(today.getFullYear() - 15, today.getMonth(), today.getDate());
    return date > minimum ? { minimumStudentAge: true } : null;
  };
}
